import type { ProductPricing } from "./index";

type CheckoutCatalogPrice = {
  id: string;
  amountCents: number;
  interval: ProductPricing["interval"];
};

type CheckoutCatalogPlan = {
  id: string;
  name: string;
  description: string;
  status: string;
  prices: CheckoutCatalogPrice[];
};

type CheckoutCatalogResponse = {
  ok?: boolean;
  plans?: CheckoutCatalogPlan[];
};

export type PaymentCheckoutRequest = {
  planId?: string;
  planName: string;
  pricing?: Pick<ProductPricing, "price" | "interval">;
  successUrl: string;
  cancelUrl: string;
};

function normalizeValue(value: string): string {
  return value.trim().toLowerCase();
}

function findCatalogPlan(
  plans: CheckoutCatalogPlan[],
  planName: string
): CheckoutCatalogPlan | null {
  const normalizedTarget = normalizeValue(planName);
  const exactMatch = plans.find((plan) => normalizeValue(plan.name) === normalizedTarget);
  if (exactMatch) return exactMatch;

  const looseMatch = plans.find((plan) => {
    const normalizedPlan = normalizeValue(plan.name);
    return normalizedPlan.includes(normalizedTarget) || normalizedTarget.includes(normalizedPlan);
  });
  return looseMatch ?? null;
}

function findCatalogPlanById(
  plans: CheckoutCatalogPlan[],
  planId?: string
): CheckoutCatalogPlan | null {
  if (!planId) {
    return null;
  }
  return plans.find((plan) => normalizeValue(plan.id) === normalizeValue(planId)) ?? null;
}

function findMatchingPrice(
  plan: CheckoutCatalogPlan,
  pricing?: Pick<ProductPricing, "price" | "interval">
): CheckoutCatalogPrice | null {
  if (!pricing) {
    return plan.prices[0] ?? null;
  }

  const matchingPrice = plan.prices.find(
    (price) => price.amountCents === pricing.price && price.interval === pricing.interval
  );
  return matchingPrice ?? plan.prices[0] ?? null;
}

export async function resolvePaymentCheckoutPriceId(input: {
  planId?: string;
  planName: string;
  pricing?: Pick<ProductPricing, "price" | "interval">;
}): Promise<string> {
  const response = await fetch("/api/website/payments/catalog", {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Unable to load payment catalog.");
  }

  const payload = (await response.json()) as CheckoutCatalogResponse;
  const plans = payload.plans ?? [];
  const plan = findCatalogPlanById(plans, input.planId) ?? findCatalogPlan(plans, input.planName);
  if (!plan) {
    const identifier = input.planId ? `${input.planName} (${input.planId})` : input.planName;
    throw new Error(`No checkout price found for ${identifier}. Contact support.`);
  }

  const price = findMatchingPrice(plan, input.pricing);
  if (!price) {
    throw new Error(`No checkout price found for ${input.planName}. Contact support.`);
  }

  return price.id;
}

export async function startPaymentCheckout(
  input: PaymentCheckoutRequest
): Promise<{ checkoutUrl: string; sessionId: string }> {
  const priceId = await resolvePaymentCheckoutPriceId({
    planId: input.planId,
    planName: input.planName,
    pricing: input.pricing,
  });
  const token = localStorage.getItem("cf-session-token");
  const response = await fetch("/api/website/payments/checkout", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "x-session-token": token } : {}),
    },
    body: JSON.stringify({
      priceId,
      successUrl: input.successUrl,
      cancelUrl: input.cancelUrl,
    }),
  });

  if (!response.ok) {
    throw new Error("Unable to start checkout.");
  }

  const payload = (await response.json()) as { checkoutUrl: string; sessionId: string };
  return {
    checkoutUrl: payload.checkoutUrl,
    sessionId: payload.sessionId,
  };
}
